'use client';

import { useTranslations } from 'next-intl';
import dynamic from 'next/dynamic';
import { useParams, useSearchParams } from 'next/navigation';
import { useMemo } from 'react';

import { FullStory } from '@/components/FullStory';
import { Amenity } from '@/lib/amenities/dto/amenity.dto';
import { GeolocalizationDto } from '@/lib/locations';
import { LocationOpeningHoursDto } from '@/lib/locations/dto/location-opening-hours.dto';
import { BlackoutDateDto } from '@/lib/shared/dto/blackout-date.dto';
import { Countries } from '@/models/Countries.enum';
import { Products } from '@/models/Products.enum';
import { OFFICE_WINDOW_IMG } from '@/models/location-utils';

import CoworkingProductsSkeleton from './Coworking/CoworkingProductsSkeleton';
import MeetingRoomSearchSkeleton from './MeetingRoom/MeetingRoomSearchSkeleton';
import OfficeSearchSkeleton from './OfficeSearch/OfficeSearchSkeleton';
import ProductDetailsCard from './ProductDetailsCard';
import { OnDemandProduct, getProductFromSearchParams } from './utils';

const CoworkingProducts = dynamic(
  () => import('./Coworking/CoworkingProducts'),
  {
    loading: () => <CoworkingProductsSkeleton />,
  }
);
const MeetingRoomSearch = dynamic(
  () => import('./MeetingRoom/MeetingRoomSearch'),
  {
    loading: () => <MeetingRoomSearchSkeleton />,
  }
);
const OfficeSearch = dynamic(() => import('./OfficeSearch/OfficeSearch'), {
  loading: () => <OfficeSearchSkeleton />,
});

type ProductSearchFactoryProps = {
  currencyIsoCode: string;
  openingHours: LocationOpeningHoursDto;
  blackoutDates: BlackoutDateDto[];
  coworkingDailyPrice: number | undefined;
  coworkingMonthlyPrice: number | undefined;
  country: Countries;
  locationCity: string;
  locationName: string;
  locationState: string;
  hasMembership: boolean;
  locationId: string;
  hasDayPass: boolean;
  isMembershipSoldOut: boolean;
  isMembershipSellableOnline: boolean;
  nearByLocationsUrl: string;
  locationGeolocalization: GeolocalizationDto;
  allMeetingRoomAmenities: Amenity[];
  products: OnDemandProduct[];
};

export function ProductSearchFactory({
  blackoutDates,
  openingHours,
  coworkingDailyPrice,
  coworkingMonthlyPrice,
  currencyIsoCode,
  country,
  hasMembership,
  hasDayPass,
  isMembershipSoldOut,
  isMembershipSellableOnline,
  nearByLocationsUrl,
  locationName,
  locationCity,
  locationState,
  locationGeolocalization,
  locationId,
  allMeetingRoomAmenities,
  products,
}: ProductSearchFactoryProps) {
  const searchParams = useSearchParams();
  const params = useParams();
  const selection: Products = getProductFromSearchParams(
    searchParams,
    products
  );
  const t = useTranslations('location.on-demand');
  const officeProduct = products.find(({ value }) => value === Products.Office);

  const officeProductBulletPoints = useMemo(
    () => [
      t('office.details-card.bullet-1'),
      t('office.details-card.bullet-2'),
      t('office.details-card.bullet-3'),
      t('office.details-card.bullet-4'),
      t('office.details-card.bullet-5'),
      t('office.details-card.bullet-6'),
    ],
    [t]
  );

  switch (selection) {
    case Products.Office:
      return (
        <>
          {/* Start FullStory recording only for location with SSOS */}
          {officeProduct && <FullStory />}
          {officeProduct?.canBuyOnline ? (
            <OfficeSearch
              blackoutDates={blackoutDates}
              openingHours={openingHours}
              locationId={locationId}
              locationName={locationName}
            />
          ) : (
            <ProductDetailsCard
              title={t('office.details-card.title')}
              bulletPoints={officeProductBulletPoints}
              image={OFFICE_WINDOW_IMG}
              ctaUrl={`/location-form/${params?.slug}`}
              ctaLabel={t('office.details-card.get-started')}
            />
          )}
        </>
      );
    case Products.MeetingRoom:
      return (
        <MeetingRoomSearch
          locationState={locationState}
          locationName={locationName}
          locationCity={locationCity}
          locationId={locationId}
          locationGeolocalization={locationGeolocalization}
          allAmenities={allMeetingRoomAmenities}
        />
      );
    case Products.Coworking:
      return (
        <CoworkingProducts
          dailyPrice={coworkingDailyPrice}
          monthlyPrice={coworkingMonthlyPrice}
          currencyIsoCode={currencyIsoCode}
          country={country}
          hasMembership={hasMembership}
          hasDayPass={hasDayPass}
          isMembershipSoldOut={isMembershipSoldOut}
          isMembershipSellableOnline={isMembershipSellableOnline}
          nearByLocationsUrl={nearByLocationsUrl}
          locationName={locationName}
          locationId={locationId}
        />
      );
    default:
      return null;
  }
}
